// src/contexts/ThemeContext.jsx - Fixed with localStorage persistence
import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage — default is LIGHT MODE (v2)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const THEME_VERSION = 'v2'; // Bump this to force a reset to light on next load
    const savedVersion  = localStorage.getItem('themeVersion');
    const savedTheme    = localStorage.getItem('theme');

    // If the user has never manually toggled the theme (no version key),
    // clear any stale dark preference and default to light.
    if (savedVersion !== THEME_VERSION) {
      localStorage.setItem('themeVersion', THEME_VERSION);
      localStorage.setItem('theme', 'light');
      return false; // light mode
    }

    return savedTheme === 'dark';
  });

  // Initialize RTL from localStorage or default to false
  const [isRTL, setIsRTL] = useState(() => {
    const savedDirection = localStorage.getItem('direction');
    return savedDirection === 'rtl';
  });

  // Apply theme to document and save to localStorage
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Apply RTL direction to document and save to localStorage
  useEffect(() => {
    const root = document.documentElement;
    
    if (isRTL) {
      root.setAttribute('dir', 'rtl');
      root.style.direction = 'rtl';
      localStorage.setItem('direction', 'rtl');
    } else {
      root.setAttribute('dir', 'ltr');
      root.style.direction = 'ltr';
      localStorage.setItem('direction', 'ltr');
    }
  }, [isRTL]);

  // Note: We intentionally do NOT follow system dark mode preference.
  // The app defaults to light mode unless the user manually switches.

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const toggleRTL = () => {
    setIsRTL(prev => !prev);
  };

  const setTheme = (theme) => {
    setIsDarkMode(theme === 'dark');
  };

  const setDirection = (direction) => {
    setIsRTL(direction === 'rtl');
  };

  const value = {
    isDarkMode,
    isRTL,
    toggleDarkMode,
    toggleRTL,
    setTheme,
    setDirection
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;